import { Accordion, AccordionSummary, AccordionDetails, Typography, Box } from '@mui/material';
import { Divider, useActiveTheme } from '@care/react-component-lib';
import sanitizeHtml from 'sanitize-html';
import he from 'he';
import { Icon24Chevron } from '@care/react-embrace-icons';
import { useResponsive } from '@/components/hooks/useResponsive';
import { getSEOSegmentationFAQs_getSEOSegmentationFAQs_faqList as SEOSegmentationQuestionAnswer } from '@/__generated__/getSEOSegmentationFAQs';
import { MakeOptional } from '@/types/utils';

type SEOSegmentationQuestionAnswerWithOptionalTypename = MakeOptional<
  SEOSegmentationQuestionAnswer,
  '__typename'
>;

type FAQListProps = {
  faqs: SEOSegmentationQuestionAnswerWithOptionalTypename[];
};

export const FAQList = ({ faqs }: FAQListProps) => {
  const { isEmbraceTheme } = useActiveTheme();
  const { isSmallViewport, isMediumViewport } = useResponsive();

  const createFaqSchema = (faqList: FAQListProps['faqs']) => {
    const mainEntity = faqList.map((faq) => {
      return {
        '@type': 'Question',
        name: `${faq.question}`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `${faq.answer}`,
        },
      };
    });

    return {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity,
    };
  };

  return (
    <Box>
      <Divider sx={{ mb: 2, mt: 0 }} />
      {faqs.map((faq, index) => (
        <Box key={`faq-${index + 1}`}>
          <Accordion
            sx={{
              boxShadow: 'none',
            }}
            disableGutters>
            <AccordionSummary
              expandIcon={<Icon24Chevron color="icon.primary" />}
              aria-controls={`panel-${index}-content`}
              id={`panel-${index}-header`}
              sx={{
                px: 0,
                '& .MuiAccordionSummary-content': {
                  minHeight: '56px',
                  display: 'flex',
                  alignItems: 'center',
                },
              }}>
              <Typography
                component="h4"
                variant={isEmbraceTheme || isSmallViewport || isMediumViewport ? 'h3' : 'h2'}
                mr={3}>
                {faq.question}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              <Typography
                variant="body2"
                sx={{
                  '& a': {
                    color: 'text.link',
                    textDecoration: 'underline',
                  },
                }}
                dangerouslySetInnerHTML={{
                  __html: sanitizeHtml(he.decode(faq.answer).replace(/\n/g, '<br>')),
                }}
              />
            </AccordionDetails>
          </Accordion>
          <Divider sx={{ my: 2 }} />
        </Box>
      ))}
      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: JSON.stringify(createFaqSchema(faqs)) }}
      />
    </Box>
  );
};
